/**
 * StochasticTiling — "Procedural Stochastic Textures by Tiling and Blending"
 * (Deliot & Heitz 2019, GPU Zen 2; Heitz's project page) in TSL, for the
 * ground albedos that otherwise read as a grid past a few tiles (shader wave
 * stage 4, 2026-09-09).
 *
 * The UV plane is cut into a lattice of equilateral triangles; every lattice
 * vertex owns a random UV offset, and a fragment samples the texture three
 * times (once per vertex of its triangle, each at that vertex's offset) and
 * blends with its barycentric weights. Blending three samples with weights
 * that sum to one lowers the contrast (the mean of three random samples is
 * closer to the texture mean), so the blend is VARIANCE-PRESERVING:
 * (Σ wᵢ cᵢ − mean) / √(Σ wᵢ²) + mean — Heitz's simplification of the
 * histogram-preserving transform for textures whose histogram is roughly
 * Gaussian (the ground albedos are). Sampling goes through `grad` with the
 * UNOFFSET uv's derivatives so mip selection ignores the random jumps.
 *
 * `hexTileWeightsCpu` is the lattice oracle the tests pin.
 */

import type * as THREE from 'three/webgpu';
import {
  Fn, float, vec2, vec3, vec4, floor, fract, uniform, texture, sqrt, dFdx, dFdy, mix,
} from 'three/tsl';

export interface StochasticTilingConfig {
  /**
   * The texture's mean colour (the variance-preserving pivot). Omit to read it
   * from the texture's coarsest mip in the shader (the 1×1 level IS the mean —
   * a fixed guess pushes dark textures below black and bright ones past white).
   */
  mean?: [number, number, number];
  /** 0 = plain weighted blend, 1 = full variance preservation (default 1). */
  contrast?: number;
  /** Lattice scale: triangles per UV unit (default 3.464 = 2/√3·3). */
  latticeScale?: number;
}

export interface StochasticTilingHandle {
  contrast: { value: number };
}

/**
 * Triangle lattice lookup: for a UV, the three vertex ids of its triangle and
 * the barycentric weights (w0 + w1 + w2 = 1). Skew the plane so the
 * equilateral lattice becomes the integer grid, then split the cell on its
 * diagonal (Deliot–Heitz, Section 4).
 */
export function hexTileWeightsCpu(u: number, v: number, latticeScale = 3.464): {
  vertices: [[number, number], [number, number], [number, number]]; weights: [number, number, number];
} {
  const su = u * latticeScale, sv = v * latticeScale;
  // skew: x' = x - y/2·(1/√3)... using the paper's matrix [1, 0; -0.57735027, 1.15470054]
  const x = su, y = -0.57735027 * su + 1.15470054 * sv;
  const bx = Math.floor(x), by = Math.floor(y);
  const fx = x - bx, fy = y - by;
  const s = 1 - fx - fy;
  if (s > 0) {
    return { vertices: [[bx, by], [bx + 1, by], [bx, by + 1]], weights: [s, fx, fy] };
  }
  return { vertices: [[bx + 1, by + 1], [bx, by + 1], [bx + 1, by]], weights: [-s, 1 - fy, 1 - fx] };
}

/** Per-vertex random offset in UV space (0..1) — a stable hash of the vertex. */
export function vertexOffsetCpu(ix: number, iy: number): [number, number] {
  const a = Math.sin(ix * 127.1 + iy * 311.7) * 43758.5453, b = Math.sin(ix * 269.5 + iy * 183.3) * 43758.5453;
  return [a - Math.floor(a), b - Math.floor(b)];
}

/** The variance-preserving blend of three samples (per channel). */
export function variancePreservingBlendCpu(c: [number, number, number], w: [number, number, number], mean: number, contrast = 1): number {
  const sum = c[0] * w[0] + c[1] * w[1] + c[2] * w[2];
  const norm = Math.sqrt(w[0] * w[0] + w[1] * w[1] + w[2] * w[2]);
  const vp = (sum - mean) / Math.max(1e-6, norm) + mean;
  return sum + (vp - sum) * contrast;
}

/**
 * Sample `map` at `uvNode` without visible repetition. Returns a vec4 node.
 * (`grad` keeps mip selection on the un-jittered uv.)
 */
export function stochasticTexture(
  map: THREE.Texture,
  uvNode: unknown,
  config?: StochasticTilingConfig,
): { node: unknown; handle: StochasticTilingHandle } {
  const uMean = config?.mean ? uniform(vec3(config.mean[0], config.mean[1], config.mean[2])) : null;
  const uContrast = uniform(config?.contrast ?? 1);
  const scale = config?.latticeScale ?? 3.464;

  const node = Fn(() => {
    const uv = vec2(uvNode as never);
    const ddx = dFdx(uv), ddy = dFdy(uv);
    const su = uv.mul(scale);
    const skew = vec2(su.x, su.x.mul(-0.57735027).add(su.y.mul(1.15470054)));
    const base = floor(skew);
    const f = fract(skew);
    const s = float(1).sub(f.x).sub(f.y);
    const up = s.greaterThan(0);
    const v0 = up.select(base, base.add(vec2(1, 1)));
    const v1 = up.select(base.add(vec2(1, 0)), base.add(vec2(0, 1)));
    const v2 = up.select(base.add(vec2(0, 1)), base.add(vec2(1, 0)));
    const w0 = up.select(s, s.negate());
    const w1 = up.select(f.x, float(1).sub(f.y));
    const w2 = up.select(f.y, float(1).sub(f.x));
    const off = (v: unknown) => {
      const vtx = vec2(v as never);
      const a = vtx.x.mul(127.1).add(vtx.y.mul(311.7)).sin().mul(43758.5453);
      const b = vtx.x.mul(269.5).add(vtx.y.mul(183.3)).sin().mul(43758.5453);
      return vec2(fract(a), fract(b));
    };
    const c0 = texture(map, uv.add(off(v0)) as never).grad(ddx as never, ddy as never);
    const c1 = texture(map, uv.add(off(v1)) as never).grad(ddx as never, ddy as never);
    const c2 = texture(map, uv.add(off(v2)) as never).grad(ddx as never, ddy as never);
    const sum = c0.rgb.mul(w0).add(c1.rgb.mul(w1)).add(c2.rgb.mul(w2));
    const norm = sqrt(w0.mul(w0).add(w1.mul(w1)).add(w2.mul(w2)));
    // The pivot: the caller's mean, else the coarsest mip (LOD clamps to the last level = the texture mean).
    const mean = uMean ?? texture(map, uv as never).level(float(24)).rgb;
    const vp = sum.sub(mean).div(norm.max(1e-4)).add(mean);
    const a = c0.a.mul(w0).add(c1.a.mul(w1)).add(c2.a.mul(w2));
    return vec4(mix(sum, vp, uContrast), a);
  })();

  return { node, handle: { contrast: uContrast as unknown as { value: number } } };
}
